// The sign-in surface, served whole by `base-auth` on the `app` surface: the session and the
// account flows a signed-out visitor reaches. Stateless calls — the session store owns what
// the SPA remembers about the caller.

import { api } from '../client'
import type { components } from '../schema'

export type UserSummary = components['schemas']['UserSummary']
export type LoginRequest = components['schemas']['LoginRequest']
export type LoginResponse = components['schemas']['LoginResponse']
export type MfaChallengeRequest = components['schemas']['MfaChallengeRequest']
export type SignupRequest = components['schemas']['SignupRequest']
export type VerifyEmailRequest = components['schemas']['VerifyEmailRequest']
export type ForgotPasswordRequest = components['schemas']['ForgotPasswordRequest']
export type ResetPasswordRequest = components['schemas']['ResetPasswordRequest']
export type UpdateMeRequest = components['schemas']['UpdateMeRequest']

/**
 * A correct password answers 200 with one of three arms, and only `established` set the
 * cookie. A wrong password and an unknown address are the same 401, by design.
 */
export function login(body: LoginRequest): Promise<LoginResponse> {
  return api.post<LoginResponse>('/auth/login', body)
}

/** The answer to a `second_factor_required` login: the same three arms again. */
export function answerMfaChallenge(body: MfaChallengeRequest): Promise<LoginResponse> {
  return api.post<LoginResponse>('/auth/mfa/challenge', body)
}

/** Always 204, signed in or not: there is nothing to refuse about ending a session. */
export function logout(): Promise<undefined> {
  return api.post<undefined>('/auth/logout')
}

/** The signed-in account; 401 when there is none. */
export function getMe(signal?: AbortSignal): Promise<UserSummary> {
  return api.get<UserSummary>('/auth/me', undefined, signal)
}

/**
 * Change what the account carries about itself — its preferred locale, and nothing else. An
 * explicit `null` clears the pick back to the platform's default. Answers the updated account.
 */
export function updateMe(body: UpdateMeRequest): Promise<UserSummary> {
  return api.patch<UserSummary>('/auth/me', body)
}

/**
 * A uniform 202 whether the address was free or taken — the answer never says. 404 when the
 * platform does not open self-registration (`auth.public_signup`).
 */
export function signup(body: SignupRequest): Promise<undefined> {
  return api.post<undefined>('/auth/signup', body)
}

/** Exactly one of `token` (the emailed link, no session needed) or `code` (needs one). */
export function verifyEmail(body: VerifyEmailRequest): Promise<undefined> {
  return api.post<undefined>('/auth/verify-email', body)
}

/** Re-send the verification message to the signed-in caller (throttled: 429). */
export function resendVerification(): Promise<undefined> {
  return api.post<undefined>('/auth/resend-verification')
}

/** A uniform 202 whether or not the address has an account. */
export function forgotPassword(body: ForgotPasswordRequest): Promise<undefined> {
  return api.post<undefined>('/auth/forgot-password', body)
}

/** Consumes the emailed link; every open session is revoked, so sign in again after it. */
export function resetPassword(body: ResetPasswordRequest): Promise<undefined> {
  return api.post<undefined>('/auth/reset-password', body)
}
